<template>
  <CmsCustomCodeRuntime v-if="isCustomCodeRuntime" :html="String(vars.customHtml ?? '')" :css="String(vars.customCss ?? '')" :js="String(vars.customJs ?? '')" :vars="vars" />
  <div v-else-if="!hasSlots" ref="rootEl" class="cms-generic-renderer" v-html="fullHtml" />
  <div v-else ref="rootEl" class="cms-generic-renderer">
    <div ref="htmlWrapperEl" v-once-html="fullHtml" style="display: contents" />
    <div ref="sourcesEl" class="cms-gr-sources">
      <div v-for="s in slotList" :key="s.pid" :data-cms-slot-src="s.pid" style="display: contents">
        <slot :name="s.name" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
/**
 * CmsGenericRenderer — Schema 驱动的通用 HTML 渲染器
 *
 * ── 无子组件模式 ──
 * 直接用 v-html 输出完整 HTML。
 *
 * ── hasChildren / slot 模式 ──
 *
 * 首次渲染通过 v-once-html 自定义指令注入 HTML（只执行 mounted，不执行 updated），
 * 后续更新完全通过手动 innerHTML 管理（watch(fullHtml) → safeUpdateHtml），
 * 在更新前保存 slot 子组件 DOM，更新后恢复到新 target。
 *
 * Slot 子组件在 source 容器中由 Vue 管理（source 用 display:none 隐藏），
 * mounted 后 relocate() 将其移入 HTML 中的占位 target div。
 * MutationObserver 监听 source 容器，确保延迟渲染的子组件也能被搬运。
 */
import { ref, computed, inject, onMounted, onUnmounted, onUpdated, watch, nextTick } from 'vue';
import type { Ref } from 'vue';
import { Liquid } from 'liquidjs';
import type { ComponentDefinition, GlobalStyleType } from '~/utils/componentDefinition';

type GlobalConfig = {
  colors?: Record<string, string>;
  typography?: Record<string, string>;
};

type CmsPageRouteContext = {
  browserPath?: string;
  browserFullUrl?: string;
  canonicalPath?: string;
  canonicalFullUrl?: string;
  pageNum?: number;
};

type PaginationItem = {
  href: string;
  isCurrent: boolean;
  isDots: boolean;
  label: string;
  page: number;
};

import CmsCustomCodeRuntime from './CmsCustomCodeRuntime.vue';
import { withDomainHeaders } from '~/composable/useDomain';
import { getRuntimeTailwindStore, shouldCompileRuntimeTailwind, MERGED_STYLE_KEY } from '~/composable/useCmsRuntimeTailwindStore';
import type { RuntimeTailwindStore } from '~/composable/useCmsRuntimeTailwindStore';
import { configureLocaleController, installLocaleController, normalizeLocale, parseLocalePath, resolveSiteAnchors } from '~/utils/localeController';
import { resolveAutoFetchConfig } from '~/utils/resolveAutoFetchConfig';
import { injectCmsFontFamilyStyleIntoFirstElement, transformCssFontFamilyToCmsVar } from '~/utils/fontFamilyUtils';
import { applyIconColors } from '~/utils/applyIconColors';
import { buildBreadcrumbItems } from '~/utils/breadcrumb';

const liquid = new Liquid({ strictVariables: false, strictFilters: false, greedy: false });
const runtimeConfig = useRuntimeConfig();
const cmsRenderPerfDebugEnabled = runtimeConfig.public.cmsRenderPerfDebug === 'true';
const SLOT_RE = /<!--\s*(?:CHILDREN|SLOT:(\S+))\s*-->/g;

const logRendererPerf = (component: string, event: string, details: Record<string, unknown> = {}) => {
  if (!cmsRenderPerfDebugEnabled) return;
  console.info('[CmsRendererPerf]', {
    component,
    event,
    ...details,
  });
};

const hashString = (input: string) => {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0).toString(36);
};

const renderLiquid = (tpl: string, vars: Record<string, unknown>): string => {
  const t = tpl.replace(/\{\{\{([^}]+)\}\}\}/g, '{{ $1 }}');
  try {
    return liquid.parseAndRenderSync(t, vars);
  } catch {
    return t;
  }
};

const parseTypographyPx = (raw: unknown): number | undefined => {
  if (raw === undefined || raw === null) return undefined;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const s = String(raw).trim();
  if (!s) return undefined;
  const n = parseInt(s.replace(/\s*px\s*$/i, '').trim(), 10);
  return Number.isFinite(n) ? n : undefined;
};

const resolveGlobalTypeData = (gc: GlobalConfig | undefined, type: GlobalStyleType): Record<string, unknown> | undefined => {
  if (!gc) return undefined;
  if (type === 'color') {
    const nested = gc.colors as Record<string, unknown> | undefined;
    if (nested && Object.keys(nested).length > 0) return nested;
    // 兼容旧数据：globalConfig 直接是扁平颜色对象
    return gc as unknown as Record<string, unknown>;
  }
  const nestedTypography = gc.typography as Record<string, unknown> | undefined;
  if (nestedTypography && Object.keys(nestedTypography).length > 0) return nestedTypography;
  // 兼容旧数据：globalConfig 直接是扁平字号对象
  return gc as unknown as Record<string, unknown>;
};

const vOnceHtml = {
  mounted(el: HTMLElement, binding: { value: string }) {
    el.innerHTML = binding.value ?? '';
  },
};

let uidSeq = 0;

const props = defineProps<{
  definition: ComponentDefinition;
  properties?: Record<string, unknown>;
}>();

const stripRuntimeOwnedProperties = (componentName: string, properties?: Record<string, unknown>): Record<string, unknown> => {
  if (!properties) return {};
  const next = { ...properties };
  if (componentName === 'CmsNotPage') delete next.products;
  if (componentName === 'CmsHero') {
    for (const key of [
      // 'showBreadcrumb',
      'currentLabel',
      'homeLabel',
      'homeHref',
      // 'breadcrumbSeparator',
      // 'breadcrumbBackgroundColor',
      // 'breadcrumbPaddingY',
    ])
      delete next[key];
  }
  return next;
};

const runtimeSafeProperties = computed<Record<string, unknown>>(() => stripRuntimeOwnedProperties(props.definition.component, props.properties));

const rootEl = ref<HTMLElement | null>(null);
const htmlWrapperEl = ref<HTMLElement | null>(null);
const sourcesEl = ref<HTMLElement | null>(null);
let jsCleanup: (() => void) | null = null;
const uid = ++uidSeq;
const runtimeTailwindCss = ref('');
const runtimeTailwindStore = getRuntimeTailwindStore();
let tailwindCssVersion = 0;
let tailwindDebounceTimer: ReturnType<typeof setTimeout> | null = null;

const autoFetchData = ref<Record<string, unknown>>({});
const eventVarOverrides = ref<Record<string, unknown>>({});
const appMode = useRuntimeConfig().public.appMode as string;
const route = useRoute();
const globalConfig = inject<Ref<GlobalConfig>>('cmsGlobalConfig', ref({}) as Ref<GlobalConfig>);
const pageRouteContext = inject<Ref<CmsPageRouteContext>>('cmsPageRouteContext', ref({}) as Ref<CmsPageRouteContext>);
const localeConfig = inject<Ref<Record<string, unknown>>>('cmsLocaleConfig', ref({}) as Ref<Record<string, unknown>>);
const previewNodeId = inject<Ref<string | null>>('previewNodeId', ref(null) as Ref<string | null>);
const lastDaisyDebugSignature = ref('');
const localFontFamily = computed(() => {
  const raw = props.properties?.fontFamily;
  return typeof raw === 'string' ? raw.trim() : '';
});

const fallbackFullUrl = (path: string) => {
  if (import.meta.client) return new URL(path || '/', window.location.origin).href;
  const requestUrl = useRequestURL();
  return new URL(path || '/', requestUrl.origin).href;
};

const routeContextValue = computed(() => {
  const ctx = pageRouteContext.value || {};
  const browserPath = String(ctx.browserPath || route.fullPath || route.path || '/').trim() || '/';
  const canonicalPath = String(ctx.canonicalPath || route.path || browserPath).trim() || '/';
  const contextPageNum = Number.isFinite(Number(ctx.pageNum)) ? Math.max(1, Math.floor(Number(ctx.pageNum))) : 1;
  const queryPageNumRaw = route.query.pageNum;
  const queryPageNumValue = Array.isArray(queryPageNumRaw) ? queryPageNumRaw[0] : queryPageNumRaw;
  const queryPageNum = Number.isFinite(Number.parseInt(String(queryPageNumValue || '').trim(), 10)) ? Math.max(1, Number.parseInt(String(queryPageNumValue || '').trim(), 10)) : 1;
  return {
    browserPath,
    canonicalPath,
    browserFullUrl: String(ctx.browserFullUrl || fallbackFullUrl(browserPath)).trim(),
    canonicalFullUrl: String(ctx.canonicalFullUrl || fallbackFullUrl(canonicalPath)).trim(),
    pageNum: Math.max(contextPageNum, queryPageNum),
  };
});

const localeContextValue = computed(() => {
  const source = localeConfig.value || {};
  const options = Array.isArray(source.options) ? source.options : [];
  const activeOptionSource = source.activeOption && typeof source.activeOption === 'object' ? (source.activeOption as Record<string, unknown>) : null;
  return {
    mainLanguage: String(source.mainLanguage || '').trim(),
    activeLanguage: String(source.activeLanguage || '').trim(),
    options,
    activeOption: activeOptionSource
      ? {
          code: String(activeOptionSource.code || '').trim(),
          label: String(activeOptionSource.label || '').trim(),
          emoji: String(activeOptionSource.emoji || '').trim(),
        }
      : null,
  };
});

const resolveStorefrontRequestLang = (pathSource: string, fallbackLang?: unknown) => {
  const pathLocale = parseLocalePath(pathSource).locale;
  const mainLanguage = localeContextValue.value.mainLanguage;
  const activeLanguage = localeContextValue.value.activeLanguage;
  // SSR 时 locale controller 会把语言写入 html[lang]，客户端 hydration 时可直接读取，
  // 不依赖任何异步数据，是最可靠的客户端 lang fallback。
  const htmlLang = import.meta.client ? document.documentElement.lang || '' : '';
  return normalizeLocale(pathLocale || mainLanguage || activeLanguage || htmlLang || fallbackLang || 'en');
};

const TEMPLATE_TOKEN_RE = /^\{\{\s*([^{}]+?)\s*\}\}$/;
const TEMPLATE_TOKEN_GLOBAL_RE = /\{\{\s*([^{}]+?)\s*\}\}/g;

const resolveRuntimeToken = (token: string, vars: Record<string, unknown>): unknown => {
  const ctx = routeContextValue.value;
  if (token === '__currentPath__') return route.path;
  if (token === '__currentUrl__') {
    if (import.meta.client) return window.location.href;
    return ctx.browserFullUrl || ctx.canonicalFullUrl || useRequestURL().href;
  }
  if (token === '__canonicalPath__') return ctx.canonicalPath;
  if (token === '__canonicalUrl__') return ctx.canonicalFullUrl;
  if (token === '__browserPath__') return ctx.browserPath;
  if (token === '__browserUrl__') return ctx.browserFullUrl;
  if (token === '__origin__') {
    try {
      return new URL(ctx.browserFullUrl || fallbackFullUrl('/')).origin;
    } catch {
      return '';
    }
  }
  if (token === '__routePageNum__') return String(ctx.pageNum);
  return vars[token] ?? '';
};

const resolveRuntimeTemplateValue = (value: unknown, vars: Record<string, unknown>, depth = 0): unknown => {
  if (typeof value !== 'string') return value;
  if (depth >= 5) return '';

  // 纯模板 {{ xxx }}：返回解析后的原始值（可能是对象/数组等非字符串）
  const fullMatch = value.match(TEMPLATE_TOKEN_RE);
  if (fullMatch) {
    const resolved = resolveRuntimeToken(fullMatch[1].trim(), vars);
    if (typeof resolved === 'string' && TEMPLATE_TOKEN_RE.test(resolved.trim())) {
      return resolveRuntimeTemplateValue(resolved.trim(), vars, depth + 1);
    }
    return resolved;
  }

  // 混合模板（如 "/{{ collection }}"）：全局查找替换，拼接为字符串
  if (TEMPLATE_TOKEN_GLOBAL_RE.test(value)) {
    return value.replace(TEMPLATE_TOKEN_GLOBAL_RE, (_, rawToken) => {
      const token = String(rawToken).trim();
      const resolved = resolveRuntimeToken(token, vars);
      if (typeof resolved === 'string' && TEMPLATE_TOKEN_RE.test(resolved.trim())) {
        return String(resolveRuntimeTemplateValue(resolved.trim(), vars, depth + 1) ?? '');
      }
      return String(resolved ?? '');
    });
  }

  return value;
};

const buildPaginationHref = (browserPath: string, page: number): string => {
  const normalizedPath = String(browserPath || '/').trim() || '/';
  const parsed = new URL(normalizedPath, 'https://cms.local');
  const basePath = parsed.pathname.replace(/\/page\/\d+\/?$/i, '').replace(/\/+$/g, '') || '/';
  const pagePath = page <= 1 ? basePath : `${basePath === '/' ? '' : basePath}/page/${page}`;
  return `${pagePath}${parsed.search}`;
};

const buildPaginationItems = (total: number, pageSize: number, currentPage: number, browserPath: string): PaginationItem[] => {
  const safePageSize = Math.max(1, Math.floor(pageSize || 1));
  const totalPages = Math.max(1, Math.ceil(Math.max(0, total) / safePageSize));
  const safeCurrentPage = Math.min(Math.max(1, Math.floor(currentPage || 1)), totalPages);
  if (totalPages <= 1) return [];

  const createPageItem = (page: number): PaginationItem => ({
    href: buildPaginationHref(browserPath, page),
    isCurrent: page === safeCurrentPage,
    isDots: false,
    label: String(page),
    page,
  });

  const items: PaginationItem[] = [];
  const addPage = (page: number) => items.push(createPageItem(page));
  const addDots = () => items.push({ href: '', isCurrent: false, isDots: true, label: '...', page: 0 });

  if (totalPages <= 5) {
    for (let page = 1; page <= totalPages; page += 1) addPage(page);
    return items;
  }

  let start = safeCurrentPage - 1;
  let end = safeCurrentPage + 1;
  if (start < 2) {
    start = 2;
    end = 4;
  }
  if (end > totalPages - 1) {
    end = totalPages - 1;
    start = totalPages - 3;
  }
  if (start < 2) start = 2;
  if (end > totalPages - 1) end = totalPages - 1;

  addPage(1);
  if (start > 2) addDots();
  for (let page = start; page <= end; page += 1) addPage(page);
  if (end < totalPages - 1) addDots();
  addPage(totalPages);

  return items;
};

const normalizeSeoTextPayload = (payload: unknown): Record<string, string> => {
  const dataRoot = payload && typeof payload === 'object' ? (((payload as Record<string, unknown>).data ?? payload) as unknown) : payload;
  const data = Array.isArray(dataRoot) ? ((dataRoot[0] as Record<string, unknown> | undefined) ?? {}) : ((dataRoot as Record<string, unknown> | undefined) ?? {});

  return {
    categoryDescription: String(data.description ?? data.metaContent ?? '').trim(),
    categoryTailDescription: String(data.tailDescription ?? data.tailDesc ?? '').trim(),
  };
};

const isDaisyDebugEnabled = computed(() => {
  const raw = route.query.debugCmsStyles;
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (String(value || '') === '1') return true;
  if (import.meta.client) {
    try {
      if (window.localStorage.getItem('debugCmsStyles') === '1') return true;
    } catch {
      // ignore storage access error
    }
  }
  return import.meta.dev;
});

const vars = computed<Record<string, unknown>>(() => {
  const merged: Record<string, unknown> = {
    ...props.definition.defaultVariables,
    ...runtimeSafeProperties.value,
    ...autoFetchData.value,
    ...eventVarOverrides.value,
  };
  const ctx = routeContextValue.value;
  merged.__pagePath__ = ctx.canonicalPath;
  merged.__currentPath__ = ctx.browserPath;
  merged.__currentUrl__ = ctx.browserFullUrl;
  merged.__browserPath__ = ctx.browserPath;
  merged.__browserUrl__ = ctx.browserFullUrl;
  merged.__canonicalPath__ = ctx.canonicalPath;
  merged.__canonicalUrl__ = ctx.canonicalFullUrl;
  merged.__routePageNum__ = String(ctx.pageNum);
  try {
    merged.__origin__ = new URL(ctx.browserFullUrl || fallbackFullUrl('/')).origin;
  } catch {
    merged.__origin__ = '';
  }
  merged.__pageCode__ = (typeof route.query.page === 'string' ? route.query.page.trim() : '') || (typeof route.query.code === 'string' ? route.query.code.trim() : '');
  if (props.definition.component === 'CmsHero') {
    merged.breadcrumbItems = buildBreadcrumbItems(ctx.canonicalPath);
  }

  const gc = globalConfig.value;
  if (gc && props.definition.globalStyleConfig) {
    const nodeProps = runtimeSafeProperties.value;
    for (const config of props.definition.globalStyleConfig) {
      const globalTypeData = resolveGlobalTypeData(gc, config.type);
      if (!globalTypeData) continue;

      for (const [globalKey, schemaKeys] of Object.entries(config.globalField)) {
        const globalValue = globalTypeData[globalKey];
        if (globalValue === undefined || globalValue === null || globalValue === '') continue;

        const targetKeys = Array.isArray(schemaKeys) ? schemaKeys : [schemaKeys];
        for (const targetKey of targetKeys) {
          const explicitly = nodeProps[targetKey];
          if (explicitly !== undefined && explicitly !== null && explicitly !== '') continue;

          if (config.type === 'typography') {
            const num = parseTypographyPx(globalValue);
            if (num !== undefined) merged[targetKey] = num;
            else merged[targetKey] = globalValue;
          } else {
            merged[targetKey] = globalValue;
          }
        }
      }
    }
  }

  if (props.definition.component === 'CmsTopUtilityBar') {
    const localeCtx = localeContextValue.value;
    const localeOptions = localeCtx.options
      .map((item) => {
        const record = item && typeof item === 'object' ? (item as Record<string, unknown>) : null;
        if (!record) return null;
        const code = String(record.code || '').trim();
        if (!code) return null;
        return {
          code,
          label: String(record.label || code).trim() || code,
          emoji: String(record.emoji || '').trim(),
        };
      })
      .filter((item): item is { code: string; label: string; emoji: string } => !!item);

    const activeCode = localeCtx.activeLanguage || String(merged.mainLanguage || localeCtx.mainLanguage || '').trim();
    const activeOption =
      localeOptions.find((item) => item.code === activeCode) ||
      (localeCtx.activeOption?.code
        ? {
            code: localeCtx.activeOption.code,
            label: localeCtx.activeOption.label || localeCtx.activeOption.code,
            emoji: localeCtx.activeOption.emoji,
          }
        : null);

    merged.languageOptions = localeOptions;
    merged.mainLanguage = localeCtx.mainLanguage || activeCode;
    merged.currentLanguageCode = activeOption?.code || activeCode;
    merged.currentLanguageLabel = activeOption?.label || activeCode;
    merged.currentLanguageEmoji = activeOption?.emoji || '';
  }

  // DaisyStat 兼容兜底：
  // 某些环境下组件定义的 globalStyleConfig 可能缺失，导致 primaryColor 未映射，
  // 数值颜色会退回到 Daisy 默认绿色。这里强制用全局色补齐主色。
  if (props.definition.component === 'DaisyStat' && gc?.colors) {
    const colors = gc.colors as Record<string, unknown>;
    const primaryExplicit = typeof merged.primaryColor === 'string' ? merged.primaryColor.trim() : '';
    const bodyExplicit = typeof merged.bodyTextColor === 'string' ? merged.bodyTextColor.trim() : '';
    const globalBody = typeof colors.bodyTextColor === 'string' ? colors.bodyTextColor.trim() : '';
    const globalButton = typeof colors.buttonBgColor === 'string' ? colors.buttonBgColor.trim() : '';
    const fallback = globalBody || globalButton;
    if (!primaryExplicit && fallback) merged.primaryColor = fallback;
    if (!bodyExplicit && globalBody) merged.bodyTextColor = globalBody;
  }

  if (import.meta.client && isDaisyDebugEnabled.value && props.definition.component.startsWith('Daisy')) {
    const sourceColors = gc?.colors && Object.keys(gc.colors as Record<string, unknown>).length > 0 ? (gc.colors as Record<string, unknown>) : (gc as unknown as Record<string, unknown> | undefined);
    const snapshot = {
      component: props.definition.component,
      pageCode: String(merged.__pageCode__ || ''),
      pagePath: String(merged.__pagePath__ || ''),
      sourcePrimary: sourceColors ? String(sourceColors.buttonBgColor ?? '') : '',
      sourceBody: sourceColors ? String(sourceColors.bodyTextColor ?? '') : '',
      propPrimary: String((props.properties as Record<string, unknown> | undefined)?.primaryColor ?? ''),
      propBody: String((props.properties as Record<string, unknown> | undefined)?.bodyTextColor ?? ''),
      mergedPrimary: String(merged.primaryColor ?? ''),
      mergedBody: String(merged.bodyTextColor ?? ''),
    };
    const sig = JSON.stringify(snapshot);
    if (sig !== lastDaisyDebugSignature.value) {
      lastDaisyDebugSignature.value = sig;
      console.info('[CMS Daisy Debug][store-renderer]', snapshot);
    }
  }

  return merged;
});

const isCustomCodeRuntime = computed(() => props.definition.component === 'CmsCustomHtml');

const hasSlots = computed(() => {
  const t = props.definition.htmlTemplate || '';
  SLOT_RE.lastIndex = 0;
  return props.definition.hasChildren || SLOT_RE.test(t);
});

interface SlotInfo {
  name: string;
  pid: string;
}

const slotList = computed<SlotInfo[]>(() => {
  if (!hasSlots.value) return [];
  const raw = props.definition.htmlTemplate || '';
  SLOT_RE.lastIndex = 0;
  const list: SlotInfo[] = [];
  const ctr: Record<string, number> = {};
  let m: RegExpExecArray | null;
  while ((m = SLOT_RE.exec(raw)) !== null) {
    const name = m[1] || 'default';
    const idx = ctr[name] || 0;
    ctr[name] = idx + 1;
    list.push({ name, pid: `cs${uid}${name}${idx}` });
  }
  return list;
});

const fullHtml = computed(() => {
  // 图标改色：icon/xxxColor 配对时将匹配 <img> 替换为 mask 单色节点
  const html = applyIconColors(renderLiquid(props.definition.htmlTemplate || '', vars.value), vars.value);
  if (!hasSlots.value) return injectCmsFontFamilyStyleIntoFirstElement(html, localFontFamily.value);
  let i = 0;
  SLOT_RE.lastIndex = 0;
  const htmlWithSlots = html.replace(SLOT_RE, () => {
    const s = slotList.value[i++];
    return s ? `<div data-cms-slot="${s.pid}" style="display:contents"></div>` : '';
  });
  return injectCmsFontFamilyStyleIntoFirstElement(htmlWithSlots, localFontFamily.value);
});

const renderedCss = computed(() => transformCssFontFamilyToCmsVar(renderLiquid(props.definition.css || '', vars.value)));

const tailwindCompileSourceHtml = computed(() => String(props.definition.htmlTemplate || '').trim());

const fetchRuntimeTailwindCss = async (html: string) => {
  const key = html.trim();
  if (!key) return '';
  const cached = runtimeTailwindStore.css.get(key);
  if (cached) return cached;

  const pending = runtimeTailwindStore.pending.get(key);
  if (pending) return pending;

  const task = $fetch<{ css?: string }>('/api/cms/custom-tailwind', {
    method: 'POST',
    body: { html: key },
  })
    .then((resp) => {
      const css = resp?.css || '';
      runtimeTailwindStore.css.set(key, css);
      runtimeTailwindStore.pending.delete(key);
      return css;
    })
    .catch((error) => {
      console.warn('[CmsGenericRenderer] runtime tailwind compile failed:', error);
      runtimeTailwindStore.pending.delete(key);
      runtimeTailwindStore.css.delete(key);
      return '';
    });

  runtimeTailwindStore.pending.set(key, task);
  return task;
};

const updateRuntimeTailwindCss = async () => {
  if (!import.meta.client || isCustomCodeRuntime.value) {
    runtimeTailwindCss.value = '';
    return;
  }
  // 有页面级合并 CSS 时，跳过单组件独立编译
  if (runtimeTailwindStore.mergedCss !== null) return;
  const html = tailwindCompileSourceHtml.value;
  if (!html) {
    runtimeTailwindCss.value = '';
    return;
  }
  if (!shouldCompileRuntimeTailwind(html)) {
    runtimeTailwindCss.value = '';
    return;
  }
  const startedAt = performance.now();
  const version = ++tailwindCssVersion;
  const css = await fetchRuntimeTailwindCss(html);
  if (version !== tailwindCssVersion) return;
  runtimeTailwindCss.value = css;
  logRendererPerf(props.definition.component, 'runtime-tailwind', {
    cssBytes: css.length,
    htmlBytes: html.length,
    durationMs: Math.round(performance.now() - startedAt),
  });
};

const scheduleRuntimeTailwindCssUpdate = () => {
  if (tailwindDebounceTimer) {
    clearTimeout(tailwindDebounceTimer);
  }
  tailwindDebounceTimer = setTimeout(() => {
    tailwindDebounceTimer = null;
    void updateRuntimeTailwindCss();
  }, 120);
};

const runtimeTailwindStyleKey = computed(() => {
  if (runtimeTailwindStore.mergedCss !== null) return MERGED_STYLE_KEY;
  return `cms-runtime-tailwind-${hashString(tailwindCompileSourceHtml.value)}`;
});

const readSsrRuntimeTailwindCss = () => {
  if (!import.meta.client) return '';
  const selector = `style[data-hid="${runtimeTailwindStyleKey.value}"]`;
  return document.head.querySelector<HTMLStyleElement>(selector)?.textContent || '';
};

const localeController = import.meta.client ? installLocaleController() : null;

const syncLocaleControllerConfig = () => {
  configureLocaleController({ mainLanguage: localeContextValue.value.mainLanguage });
};

if (import.meta.client) {
  watch(localeContextValue, syncLocaleControllerConfig, { immediate: true });
}

if (import.meta.server) {
  const html = tailwindCompileSourceHtml.value;
  if (runtimeTailwindStore.mergedCss !== null) {
    // plugin 已完成页面级合并编译，直接复用
    runtimeTailwindCss.value = runtimeTailwindStore.mergedCss;
  } else {
    runtimeTailwindCss.value = shouldCompileRuntimeTailwind(html) ? await fetchRuntimeTailwindCss(html) : '';
  }
}

if (import.meta.client) {
  // 首个实例初始化时从 DOM 读取合并 style，后续实例直接用 store
  if (runtimeTailwindStore.mergedCss === null) {
    const mergedEl = document.head.querySelector<HTMLStyleElement>(`style[data-hid="${MERGED_STYLE_KEY}"]`);
    if (mergedEl) {
      runtimeTailwindStore.mergedCss = mergedEl.textContent || '';
    }
  }
  runtimeTailwindCss.value = runtimeTailwindStore.mergedCss !== null ? runtimeTailwindStore.mergedCss : readSsrRuntimeTailwindCss();
}

// 非合并模式下，将各组件编译结果写入 html→css 缓存
if (runtimeTailwindStore.mergedCss === null && tailwindCompileSourceHtml.value && runtimeTailwindCss.value) {
  runtimeTailwindStore.css.set(tailwindCompileSourceHtml.value, runtimeTailwindCss.value);
}

useHead(
  computed(() => {
    const styles: Array<{ key: string; innerHTML: string }> = [];
    if (renderedCss.value) {
      styles.push({ key: `cms-style-${props.definition.component}`, innerHTML: renderedCss.value });
    }
    if (runtimeTailwindCss.value) {
      styles.push({ key: runtimeTailwindStyleKey.value, innerHTML: runtimeTailwindCss.value });
    }
    if (!styles.length) return {};
    return {
      style: styles,
    };
  }),
);

const jsLogicDebugContext = computed(() => ({
  component: props.definition.component,
  nodeId: previewNodeId.value,
  jsLogic: props.definition.jsLogic,
  properties: props.properties ?? {},
}));

const runJs = () => {
  killJs();
  const el = rootEl.value?.firstElementChild as HTMLElement | null;
  if (!el || !props.definition.jsLogic) return;
  const startedAt = performance.now();
  try {
    const wrapper = new Function('return ' + props.definition.jsLogic); // eslint-disable-line no-new-func
    const fn = wrapper();
    if (typeof fn === 'function') {
      const r = fn.call(el, el, vars.value);
      if (typeof r === 'function') jsCleanup = r;
      logRendererPerf(props.definition.component, 'js-logic', {
        durationMs: Math.round(performance.now() - startedAt),
        hasCleanup: typeof r === 'function',
      });
    }
  } catch (e) {
    console.warn('[CmsGenericRenderer] jsLogic error:', {
      component: props.definition.component,
      displayName: props.definition.displayName,
      error: e,
      jsLogicPreview: props.definition.jsLogic.slice(0, 500),
    });
  }
};
const killJs = () => {
  if (jsCleanup) {
    try {
      jsCleanup();
    } catch {}
    jsCleanup = null;
  }
};

let anchorNormalizeTimer: number | null = null;
let anchorObserver: MutationObserver | null = null;
let normalizingAnchors = false;

const normalizeRenderedAnchors = () => {
  if (normalizingAnchors) return;
  const root = rootEl.value;
  if (!root) return;
  normalizingAnchors = true;
  try {
    resolveSiteAnchors(root);
  } finally {
    normalizingAnchors = false;
  }
};

const scheduleAnchorNormalization = () => {
  if (!import.meta.client) return;
  if (anchorNormalizeTimer) window.clearTimeout(anchorNormalizeTimer);
  void nextTick(() => {
    window.requestAnimationFrame(() => {
      normalizeRenderedAnchors();
      anchorNormalizeTimer = window.setTimeout(normalizeRenderedAnchors, 80);
    });
  });
};

const startAnchorObserver = () => {
  stopAnchorObserver();
  if (!import.meta.client) return;
  const root = rootEl.value;
  if (!root) return;
  anchorObserver = new MutationObserver((mutations) => {
    if (normalizingAnchors) return;
    const shouldNormalize = mutations.some((mutation) => {
      if (mutation.type === 'attributes') return mutation.attributeName === 'href';
      return Array.from(mutation.addedNodes).some((node) => {
        if (!(node instanceof HTMLElement)) return false;
        return node.matches?.('a[href]') || Boolean(node.querySelector?.('a[href]'));
      });
    });
    if (shouldNormalize) scheduleAnchorNormalization();
  });
  anchorObserver.observe(root, { childList: true, subtree: true, attributes: true, attributeFilter: ['href'] });
};

const stopAnchorObserver = () => {
  if (anchorObserver) {
    anchorObserver.disconnect();
    anchorObserver = null;
  }
  if (anchorNormalizeTimer) {
    window.clearTimeout(anchorNormalizeTimer);
    anchorNormalizeTimer = null;
  }
};

/**
 * relocate：将 slot 源容器中的子组件 DOM 搬运到 HTML 模板中对应的 target 位置。
 */
let relocating = false;
const relocate = () => {
  if (relocating) return;
  const root = rootEl.value;
  const sources = sourcesEl.value;
  if (!root || !sources) return;
  relocating = true;
  try {
    const srcContainers = sources.children;
    for (let i = 0; i < srcContainers.length; i++) {
      const source = srcContainers[i] as HTMLElement;
      const pid = source.dataset.cmsSlotSrc;
      if (!pid || !source.firstChild) continue;
      const target = root.querySelector<HTMLElement>(`[data-cms-slot="${pid}"]`);
      if (!target) continue;
      while (source.firstChild) target.appendChild(source.firstChild);
    }
  } finally {
    relocating = false;
    scheduleAnchorNormalization();
  }
};

let slotObserver: MutationObserver | null = null;
const startSlotObserver = () => {
  stopSlotObserver();
  const sources = sourcesEl.value;
  if (!sources) return;
  slotObserver = new MutationObserver(() => {
    if (!relocating) relocate();
  });
  slotObserver.observe(sources, { childList: true, subtree: true });
};
const stopSlotObserver = () => {
  if (slotObserver) {
    slotObserver.disconnect();
    slotObserver = null;
  }
};

/**
 * 安全更新 HTML wrapper：
 * 保存 target 中的 slot DOM → 更新 innerHTML → 恢复 slot DOM 到新 target
 */
const safeUpdateHtml = () => {
  const wrapper = htmlWrapperEl.value;
  const root = rootEl.value;
  if (!wrapper || !root) return;

  const savedNodes = new Map<string, DocumentFragment>();
  for (const s of slotList.value) {
    const target = root.querySelector<HTMLElement>(`[data-cms-slot="${s.pid}"]`);
    if (!target || !target.firstChild) continue;
    const frag = document.createDocumentFragment();
    while (target.firstChild) frag.appendChild(target.firstChild);
    savedNodes.set(s.pid, frag);
  }

  resolveSiteAnchors(root);

  for (const s of slotList.value) {
    const frag = savedNodes.get(s.pid);
    if (!frag) continue;
    const target = root.querySelector<HTMLElement>(`[data-cms-slot="${s.pid}"]`);
    if (target) {
      target.appendChild(frag);
    } else {
      const sources = sourcesEl.value;
      if (!sources) continue;
      const source = sources.querySelector<HTMLElement>(`[data-cms-slot-src="${s.pid}"]`);
      if (source) source.appendChild(frag);
    }
  }
  savedNodes.clear();
  scheduleAnchorNormalization();
};

const applyAutoFetchTransforms = (mapped: Record<string, unknown>, transforms?: Record<string, Record<string, unknown>>) => {
  if (!transforms) return;
  for (const [field, t] of Object.entries(transforms)) {
    if (t.type === 'fallback') {
      const candidates = [...(t.field ? [String(t.field)] : []), ...(Array.isArray(t.fields) ? (t.fields as string[]) : [])];
      if (!mapped[field] || String(mapped[field]).trim() === '') {
        for (const fb of candidates) {
          const v = mapped[fb];
          if (v && String(v).trim() !== '') {
            mapped[field] = v;
            break;
          }
        }
      }
      for (const fb of candidates) delete mapped[fb];
      if (typeof t.length === 'number')
        mapped[field] = String(mapped[field] ?? '')
          .trim()
          .slice(0, t.length as number);
    }
    if (t.type === 'stripHtml') {
      const raw = String(mapped[field] ?? '');
      const stripped = raw
        .replace(/<[^>]+>/g, ' ')
        .replace(/\s+/g, ' ')
        .trim();
      mapped[field] = typeof t.maxLength === 'number' ? stripped.slice(0, t.maxLength) : stripped;
    }
    if (t.type === 'dateTruncate') {
      const len = typeof t.length === 'number' ? t.length : 10;
      mapped[field] = String(mapped[field] ?? '')
        .trim()
        .slice(0, len);
    }
    if (t.type === 'hrefTemplate' && typeof t.template === 'string') {
      const href = t.template.replace(/\{\{(\w+)\}\}/g, (_: string, k: string) => String(mapped[k] ?? ''));
      mapped[field] = href;
    }
    if (t.type === 'galleryImages') {
      const coverKey = t.coverField as string;
      const galleryKey = t.galleryField as string;
      const altKey = t.altField as string;
      const coverUrl = String(mapped[coverKey] ?? '').trim();
      const altText = String(mapped[altKey] ?? '').trim();
      let rawGallery = mapped[galleryKey];
      if (typeof rawGallery === 'string') {
        const galleryText = rawGallery;
        try {
          rawGallery = JSON.parse(galleryText);
        } catch {
          rawGallery = galleryText.split(',');
        }
      }
      const urls: string[] = Array.isArray(rawGallery)
        ? (rawGallery as unknown[]).flatMap((u) => {
            if (typeof u === 'string') return u.trim() ? [u.trim()] : [];
            if (!u || typeof u !== 'object' || Array.isArray(u)) return [];
            const row = u as Record<string, unknown>;
            const src = String(row.url ?? row.src ?? row.image ?? row.imageUrl ?? row.pic ?? row.fileUrl ?? '').trim();
            return src ? [src] : [];
          })
        : [];
      if (!urls.length && coverUrl) urls.push(coverUrl);
      mapped[field] = urls.map((src, i) => ({ id: `detail-image-${i}`, image: src, src, alt: altText || '', active: i === 0 }));
      if (coverKey.startsWith('_')) delete mapped[coverKey];
      if (galleryKey.startsWith('_')) delete mapped[galleryKey];
      if (altKey.startsWith('_')) delete mapped[altKey];
    }
    if (t.type === 'htmlToLines') {
      const raw = String(mapped[t.field as string] ?? '');
      const stripped = raw.replace(/<[^>]+>/g, '\n').replace(/&nbsp;/gi, ' ');
      const lines = stripped
        .split('\n')
        .map((l) => l.trim())
        .filter((l) => l);
      mapped[field] = lines;
      delete mapped[t.field as string];
    }
    if (t.type === 'keyValueList') {
      const candidates = [field, ...(Array.isArray(t.fields) ? (t.fields as string[]) : [])];
      let source: unknown;
      for (const key of candidates) {
        const value = mapped[key];
        if (Array.isArray(value) || (value && typeof value === 'object') || String(value ?? '').trim()) {
          source = value;
          break;
        }
      }
      if (typeof source === 'string') {
        try {
          source = JSON.parse(source);
        } catch {
          /* keep raw string */
        }
      }
      if (Array.isArray(source)) {
        mapped[field] = source
          .map((item) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
              const row = item as Record<string, unknown>;
              const label = String(row.label ?? row.name ?? row.key ?? row.attrName ?? row.title ?? '').trim();
              const value = String(row.value ?? row.attrValue ?? row.content ?? row.text ?? '').trim();
              return label || value ? { label, value } : null;
            }
            return null;
          })
          .filter(Boolean);
      } else if (source && typeof source === 'object') {
        mapped[field] = Object.entries(source as Record<string, unknown>)
          .map(([label, value]) => ({ label, value: String(value ?? '') }))
          .filter((item) => item.label || item.value);
      }
      for (const key of candidates) if (key.startsWith('_')) delete mapped[key];
    }
    if (t.type === 'joinValues') {
      const candidates = [field, ...(Array.isArray(t.fields) ? (t.fields as string[]) : [])];
      const parts = candidates.flatMap((key) => {
        const value = mapped[key];
        if (Array.isArray(value)) return value.map((item) => String(item ?? '').trim()).filter(Boolean);
        if (value && typeof value === 'object')
          return Object.values(value as Record<string, unknown>)
            .map((item) => String(item ?? '').trim())
            .filter(Boolean);
        const text = String(value ?? '').trim();
        return text ? [text] : [];
      });
      if (parts.length) mapped[field] = Array.from(new Set(parts)).join(' / ');
      for (const key of candidates) if (key.startsWith('_')) delete mapped[key];
    }
    if (t.type === 'descriptionBlocks') {
      const candidates = [field, ...(Array.isArray(t.fields) ? (t.fields as string[]) : [])];
      let source: unknown;
      for (const key of candidates) {
        const value = mapped[key];
        if (Array.isArray(value) || String(value ?? '').trim()) {
          source = value;
          break;
        }
      }
      if (typeof source === 'string') {
        try {
          source = JSON.parse(source);
        } catch {
          /* keep raw string */
        }
      }
      if (Array.isArray(source)) {
        mapped[field] = source
          .map((item) => {
            if (item && typeof item === 'object' && !Array.isArray(item)) {
              const row = item as Record<string, unknown>;
              const title = String(row.title ?? row.name ?? '').trim();
              const text = String(row.text ?? row.content ?? row.description ?? '').trim();
              return title || text ? { title, text } : null;
            }
            const text = String(item ?? '').trim();
            return text ? { title: '', text } : null;
          })
          .filter(Boolean);
      } else {
        const text = String(source ?? '')
          .replace(/<[^>]+>/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
        if (text) mapped[field] = [{ title: '', text }];
      }
      for (const key of candidates) if (key.startsWith('_')) delete mapped[key];
    }
    if (t.type === 'featureImages') {
      const candidates = [field, ...(Array.isArray(t.fields) ? (t.fields as string[]) : [])];
      const altText = String(mapped[t.altField as string] ?? '').trim();
      let urls: string[] = [];
      for (const key of candidates) {
        const raw = mapped[key];
        let source = raw;
        if (typeof source === 'string') {
          const sourceText = source;
          try {
            source = JSON.parse(sourceText);
          } catch {
            source = sourceText.split(',');
          }
        }
        urls = Array.isArray(source)
          ? (source as unknown[]).flatMap((item) => {
              if (typeof item === 'string') return item.trim() ? [item.trim()] : [];
              if (!item || typeof item !== 'object' || Array.isArray(item)) return [];
              const row = item as Record<string, unknown>;
              const image = String(row.url ?? row.src ?? row.image ?? row.imageUrl ?? row.pic ?? row.fileUrl ?? '').trim();
              return image ? [image] : [];
            })
          : [];
        if (urls.length) break;
      }
      if (urls.length) {
        mapped[field] = urls.slice(0, 2).map((image, index) => ({
          image,
          alt: altText || `Product image ${index + 1}`,
          title: '',
          text: '',
        }));
      }
      for (const key of candidates) if (key.startsWith('_')) delete mapped[key];
    }
  }
};

const mapAutoFetchSingleField = (targetField: string, raw: Record<string, unknown>, sourceField: string): unknown => {
  const v = raw[sourceField];
  if (Array.isArray(v)) return v;
  if (v !== undefined && v !== null) return v;
  if (/(List|Pages)$/i.test(targetField)) return [];
  return '';
};

const UNRESOLVED_TEMPLATE_RE = /\{\{\s*[^}]+\s*\}\}/;

const hasUnresolvedTemplate = (value: unknown): boolean => {
  if (typeof value !== 'string') return false;
  return UNRESOLVED_TEMPLATE_RE.test(value);
};

const isEmptyQueryValue = (value: unknown): boolean => {
  if (value === undefined || value === null) return true;
  if (typeof value === 'string') return value.trim() === '' || hasUnresolvedTemplate(value);
  return false;
};

const buildEmptyAutoFetchPayload = (af: NonNullable<ReturnType<typeof resolveAutoFetchConfig>>): Record<string, unknown> => {
  if (af.mode === 'single') {
    return Object.fromEntries(Object.keys(af.fieldMapping || {}).map((field) => [field, /(List|Pages)$/i.test(field) ? [] : '']));
  }
  return af.targetKey ? { [af.targetKey]: [] } : {};
};

const warnAutoFetchSkipped = (reason: string, af: NonNullable<ReturnType<typeof resolveAutoFetchConfig>>, query: Record<string, unknown> | undefined) => {
  if (!import.meta.server) return;
  console.warn('[CmsGenericRenderer] autoFetch skipped:', {
    component: props.definition.component,
    endpoint: af.endpoint,
    reason,
    route: route.fullPath,
    query,
  });
};

const validateAutoFetchRequest = (af: NonNullable<ReturnType<typeof resolveAutoFetchConfig>>, query: Record<string, unknown> | undefined): string | null => {
  if (appMode === 'storefront' && !String(af.endpoint || '').startsWith('/api/')) {
    return 'storefront endpoint must be a relative /api path';
  }

  const requiredKeys = Array.isArray(af.requiredQueryKeys) ? af.requiredQueryKeys : [];
  for (const key of requiredKeys) {
    if (isEmptyQueryValue(query?.[key])) return `required query "${key}" is empty or unresolved`;
  }

  const unresolvedEntries = Object.entries(query || {}).filter(([, value]) => hasUnresolvedTemplate(value));
  if (unresolvedEntries.length > 0) {
    return `query contains unresolved template: ${unresolvedEntries.map(([key]) => key).join(', ')}`;
  }

  return null;
};

const buildResolvedAutoFetchQuery = (af: NonNullable<ReturnType<typeof resolveAutoFetchConfig>>, currentVars: Record<string, unknown>): Record<string, unknown> | undefined => {
  if (!af.query) return undefined;
  const entries = Object.entries(af.query)
    .map(([k, v]) => [k, resolveRuntimeTemplateValue(v, currentVars)] as const)
    .filter(([_, v]) => v !== '' && v != null);
  return entries.length > 0 ? Object.fromEntries(entries) : undefined;
};

const resolveAutoFetch = async (): Promise<Record<string, unknown>> => {
  const af = resolveAutoFetchConfig(props.definition.autoFetch, appMode);
  if (!af) return {};
  try {
    const currentVars = {
      ...props.definition.defaultVariables,
      ...props.properties,
      ...eventVarOverrides.value,
    };
    const resolvedQuery = buildResolvedAutoFetchQuery(af, currentVars);

    const fetchOptions: Record<string, unknown> = {
      method: 'GET',
      query: resolvedQuery,
    };
    let routePathForLang = '';
    let routeLocaleForFetch = '';
    if (import.meta.client) {
      routePathForLang = `${window.location.pathname}${window.location.search}${window.location.hash}` || '/';
      routeLocaleForFetch = resolveStorefrontRequestLang(routePathForLang);
      fetchOptions.headers = withDomainHeaders({
        lang: routeLocaleForFetch,
        'x-soho-route-lang': routeLocaleForFetch,
        'x-soho-route-path': routePathForLang,
      });
    } else {
      const serverHeaders = useRequestHeaders(['host', 'x-forwarded-host', 'lang']);
      const routePathSource = (() => {
        const ctx = routeContextValue.value;
        const fullUrl = String(ctx.browserFullUrl || ctx.canonicalFullUrl || '').trim();
        if (fullUrl) {
          try {
            const parsed = new URL(fullUrl);
            return `${parsed.pathname}${parsed.search}${parsed.hash}` || '/';
          } catch {
            return fullUrl;
          }
        }
        if (route.fullPath) return route.fullPath;
        const requestUrl = useRequestURL();
        return `${requestUrl.pathname}${requestUrl.search}${requestUrl.hash}` || '/';
      })();
      const routeLocale = resolveStorefrontRequestLang(routePathSource, serverHeaders.lang);
      routePathForLang = routePathSource;
      routeLocaleForFetch = routeLocale;
      fetchOptions.headers = withDomainHeaders({
        ...serverHeaders,
        lang: routeLocale,
        'x-soho-route-lang': routeLocale,
        'x-soho-route-path': routePathSource,
      });
    }

    const enrichedQuery = {
      ...(resolvedQuery || {}),
      lang: routeLocaleForFetch || 'en',
      browserPath: routePathForLang || '/',
    };

    const validationError = validateAutoFetchRequest(af, enrichedQuery);
    if (validationError) {
      warnAutoFetchSkipped(validationError, af, enrichedQuery);
      return buildEmptyAutoFetchPayload(af);
    }

    fetchOptions.query = enrichedQuery;

    const autoFetchStartedAt = performance.now();
    logRendererPerf(props.definition.component, 'auto-fetch:start', {
      endpoint: af.endpoint,
      query: enrichedQuery,
    });

    const resp = await $fetch<Record<string, unknown>>(af.endpoint, fetchOptions);

    logRendererPerf(props.definition.component, 'auto-fetch:response', {
      endpoint: af.endpoint,
      durationMs: Math.round(performance.now() - autoFetchStartedAt),
      responseBytes: JSON.stringify(resp).length,
    });

    const enrichStorefrontPayload = async (payload: Record<string, unknown>, totalRaw?: unknown): Promise<Record<string, unknown>> => {
      if (!(appMode === 'storefront' && import.meta.server)) return payload;

      const enriched = { ...payload };
      const pageSize = Math.max(1, Number(resolvedQuery?.pageSize ?? props.properties?.pageSize ?? props.definition.defaultVariables?.pageSize ?? 1) || 1);
      const currentPage = Math.max(1, Number(resolvedQuery?.pageNum ?? routeContextValue.value.pageNum ?? 1) || 1);
      const total = Number(enriched._total ?? totalRaw ?? 0) || 0;
      enriched.currentPage = currentPage;
      enriched.paginationItems = buildPaginationItems(total, pageSize, currentPage, routeContextValue.value.browserPath || route.fullPath || '/');

      if (['CmsBlogPostList', 'CmsCategoryProductList'].includes(props.definition.component)) {
        const fullUrl = String(routeContextValue.value.browserFullUrl || routeContextValue.value.canonicalFullUrl || '').trim();
        if (fullUrl) {
          try {
            const seoResp = await $fetch<Record<string, unknown>>('/api/tenant-product-category-description-by-url', {
              method: 'GET',
              query: { url: fullUrl },
              headers: fetchOptions.headers as Record<string, string> | undefined,
            });
            Object.assign(enriched, normalizeSeoTextPayload(seoResp));
          } catch (error) {
            console.warn('[CmsGenericRenderer] storefront seo fetch error:', error);
          }
        }
      }

      return enriched;
    };

    if (af.mode === 'single') {
      const rowRaw = resp?.data != null && typeof resp.data === 'object' && !Array.isArray(resp.data) ? (resp.data as Record<string, unknown>) : (resp as Record<string, unknown>);
      const mapped: Record<string, unknown> = {};
      for (const [targetField, sourceField] of Object.entries(af.fieldMapping)) {
        mapped[targetField] = mapAutoFetchSingleField(targetField, rowRaw, sourceField as string);
      }
      applyAutoFetchTransforms(mapped, af.transforms as Record<string, Record<string, unknown>> | undefined);
      return enrichStorefrontPayload(mapped);
    }

    const tk = af.targetKey;
    if (!tk) return {};
    const rowsPath = af.rowsPath || 'rows';
    let rows = resp?.[rowsPath] as unknown[] | undefined;
    if (!Array.isArray(rows)) {
      const nested = resp?.data as Record<string, unknown> | undefined;
      rows = nested?.[rowsPath] as unknown[] | undefined;
    }
    if (!Array.isArray(rows)) return {};
    const totalRaw = resp?.total ?? (resp?.data as Record<string, unknown> | undefined)?.total;
    const fetchResult: Record<string, unknown> = {
      [tk]: rows.map((row: unknown) => {
        const r = row as Record<string, unknown>;
        const mapped: Record<string, unknown> = {};
        for (const [targetField, sourceField] of Object.entries(af.fieldMapping)) {
          mapped[targetField] = r[sourceField] ?? '';
        }
        applyAutoFetchTransforms(mapped, af.transforms as Record<string, Record<string, unknown>> | undefined);
        return mapped;
      }),
    };
    if (totalRaw != null) fetchResult._total = Number(totalRaw) || 0;
    return enrichStorefrontPayload(fetchResult, totalRaw);
  } catch (e) {
    console.warn('[CmsGenericRenderer] autoFetch error:', e);
    return {};
  }
};

const autoFetchKey = computed(() => {
  const af = resolveAutoFetchConfig(props.definition.autoFetch, appMode);
  if (!af) return `af-noop-${props.definition.component}`;

  const currentVars = {
    ...props.definition.defaultVariables,
    ...props.properties,
    ...eventVarOverrides.value,
  };
  const resolvedQuery = buildResolvedAutoFetchQuery(af, currentVars);
  const ctx = routeContextValue.value;
  const identity = {
    component: props.definition.component,
    nodeId: previewNodeId.value || '',
    endpoint: af.endpoint,
    mode: af.mode || 'list',
    targetKey: af.targetKey || '',
    browserPath: ctx.browserPath,
    canonicalPath: ctx.canonicalPath,
    pageNum: ctx.pageNum,
    query: resolvedQuery || {},
  };

  return `af-${props.definition.component}-${hashString(JSON.stringify(identity))}`;
});

const syncAutoFetchData = (value: Record<string, unknown> | null | undefined) => {
  autoFetchData.value = value && Object.keys(value).length > 0 ? value : {};
};

const { data: _afData } = await useAsyncData(autoFetchKey.value, () => resolveAutoFetch(), appMode === 'storefront' ? { lazy: false, server: true } : { lazy: false });
syncAutoFetchData(_afData.value);
watch(_afData, (v) => {
  syncAutoFetchData(v || undefined);
});

let eventCleanup: (() => void) | null = null;

const setupListenEvent = () => {
  if (appMode === 'storefront') return;
  if (eventCleanup) {
    eventCleanup();
    eventCleanup = null;
  }
  const le = resolveAutoFetchConfig(props.definition.autoFetch, appMode)?.listenEvent;
  if (!le) return;
  const handler = (e: Event) => {
    const detail = (e as CustomEvent).detail || {};
    const patch: Record<string, unknown> = {};
    for (const [varKey, detailKey] of Object.entries(le.varMapping)) {
      const val = (detail as Record<string, unknown>)[detailKey];
      if (val != null && String(val).trim() !== '') {
        patch[varKey] = val;
      }
    }
    if (Object.keys(patch).length > 0) {
      eventVarOverrides.value = { ...eventVarOverrides.value, ...patch };
    }
  };
  document.addEventListener(le.event, handler);
  eventCleanup = () => document.removeEventListener(le.event, handler);
};

onMounted(() => {
  if (localeController) localeController.applyHtmlLang(localeController.getCurrentLocale());
  relocate();
  startSlotObserver();
  startAnchorObserver();
  setupListenEvent();
  if (runtimeTailwindStore.mergedCss === null && !runtimeTailwindStore.css.get(tailwindCompileSourceHtml.value)) {
    void updateRuntimeTailwindCss();
  }
  if (!isCustomCodeRuntime.value) runJs();
  scheduleAnchorNormalization();
});

onUpdated(() => {
  if (hasSlots.value) relocate();
  scheduleAnchorNormalization();
});

onUnmounted(() => {
  killJs();
  stopSlotObserver();
  stopAnchorObserver();
  if (tailwindDebounceTimer) {
    clearTimeout(tailwindDebounceTimer);
    tailwindDebounceTimer = null;
  }
  if (eventCleanup) {
    eventCleanup();
    eventCleanup = null;
  }
});

watch(
  () => props.definition.component,
  () => {
    if (isCustomCodeRuntime.value) return;
    scheduleRuntimeTailwindCssUpdate();
    if (rootEl.value) {
      killJs();
      nextTick(runJs);
    }
    scheduleAnchorNormalization();
  },
);

const pageContextKey = computed(() => `${vars.value.__pageCode__}|${vars.value.__pagePath__}`);

watch(fullHtml, () => {
  if (isCustomCodeRuntime.value) return;
  if (!rootEl.value) return;
  if (hasSlots.value) {
    safeUpdateHtml();
  }
  if (props.definition.jsLogic) {
    killJs();
    nextTick(runJs);
  }
  scheduleAnchorNormalization();
});

watch(
  vars,
  () => {
    if (isCustomCodeRuntime.value) return;
    if (props.definition.jsLogic) {
      killJs();
      nextTick(runJs);
    }
    scheduleAnchorNormalization();
  },
  { deep: true },
);

watch(pageContextKey, () => {
  if (!isCustomCodeRuntime.value && props.definition.jsLogic) {
    killJs();
    nextTick(runJs);
  }
  scheduleAnchorNormalization();
});

const autoFetchQuerySignature = computed(() => {
  const af = resolveAutoFetchConfig(props.definition.autoFetch, appMode);
  if (!af?.query) return '';
  const v = {
    ...props.definition.defaultVariables,
    ...props.properties,
    ...eventVarOverrides.value,
  };
  return Object.entries(af.query)
    .map(([k, val]) => {
      if (typeof val === 'string' && val.startsWith('{{') && val.endsWith('}}')) {
        const varKey = val.slice(2, -2).trim();
        return `${k}=${resolveRuntimeToken(varKey, v)}`;
      }
      return `${k}=${val}`;
    })
    .join('&');
});

watch(autoFetchQuerySignature, async () => {
  if (appMode === 'storefront' && import.meta.server) return;
  const nextData = await resolveAutoFetch();
  if (Object.keys(nextData).length > 0) {
    autoFetchData.value = nextData;
  }
  if (!isCustomCodeRuntime.value && props.definition.jsLogic) {
    killJs();
    nextTick(runJs);
  }
  scheduleAnchorNormalization();
});

watch(
  () => props.definition.htmlTemplate,
  () => {
    if (isCustomCodeRuntime.value) return;
    scheduleRuntimeTailwindCssUpdate();
    scheduleAnchorNormalization();
  },
);
</script>

<style scoped>
.cms-generic-renderer {
  width: 100%;
  display: contents;
}

.cms-gr-sources {
  display: none !important;
}
</style>

<!-- 非 scoped：作用于 v-html 渲染的图标改色节点（applyIconColors 产物），勿加 scoped -->
<style>
.cms-icon-mono {
  display: inline-block;
  width: 1em;
  height: 1em;
  background-color: var(--icon-color, currentColor);
  -webkit-mask: var(--icon-url) center / contain no-repeat;
  mask: var(--icon-url) center / contain no-repeat;
}
</style>
